import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { Core } from '@nsftx/systems-sdk'
import { logService } from '@nsftx/seven-client-retail-utils'
import errorParser from '#src/common/error-handling/errorParser'
import { useDeviceManagementStore } from '#src/stores/deviceManagementStore'
import DMCoreError from '#src/classes/DMCoreError'
import { getAppData } from '#src/services/appDataService'
import { handleDeviceAccessTokenChange } from './deviceManagementTokenService'
import type DMInitConfig from '#src/interfaces/DMInitConfig'
import { useWorkingTimeStore } from '#src/stores/workingTimeStore'
import { initDmDisplay } from './deviceManagementDisplayService'
import DMDisplayError from '#src/classes/DMDisplayError'

const LOG_PREFIX = '[deviceManagementService]'
let core: Core | undefined

const handleWorkingTimeUpdate = (appRunUuid: string) => {
  logService.info(`${LOG_PREFIX} location_work_time_update event received`, {
    app_run_uuid: appRunUuid,
    code: 'DM_LOCATION_WORK_TIME_UPDATE_RECEIVED'
  })
  getCore()
    .deviceState()
    .then((deviceStateData) => {
      const workingTimeStore = useWorkingTimeStore()
      workingTimeStore.setWorkingTimeData(deviceStateData.location.working_time)
    })
    .catch((error) => {
      logService.error(`${LOG_PREFIX} Fetching device state on working time update has failed.`, {
        ...errorParser.parseUpstream(error),
        code: 'DM_FETCH_DEVICE_STATE_ON_WORK_TIME_UPDATE_ERROR'
      })
    })
}

const initDeviceManagement = ({
  appName,
  appRunUuid,
  autoSetDeviceAccessToken = ref(true)
}: DMInitConfig) =>
  new Promise<void>((resolve, reject) => {
    const url = import.meta.env.VITE_DM_APPLICATION_CONFIG_BASE_URL
    const { isDmReady } = storeToRefs(useDeviceManagementStore())

    isDmReady.value = false
    core = new Core(appName, `${url}/centralHub`)

    core.connect().catch((error: Error) => {
      const parsedError = errorParser.parseUpstream(error)
      const code = 'DM_CORE_CONNECTION_ERROR'
      logService.error(`${LOG_PREFIX} Core connect has failed`, {
        ...parsedError,
        code,
        details: {
          url,
          appName,
          appRunUuid
        }
      })
      reject(
        new DMCoreError(parsedError.upstream_message, code, {
          cause: error
        })
      )
    })

    core.on('connection', () => {
      logService.info(`${LOG_PREFIX} Core connection event received`, {
        app_run_uuid: appRunUuid,
        code: 'DM_CORE_CONNECTION_SUCCESS'
      })

      // this is required to run and wait
      initDmDisplay(core!, appRunUuid)
        .then(() => {
          isDmReady.value = true
          getAppData(getCore(), appRunUuid).then(resolve).catch(reject)
        })
        .catch((e) => {
          reject(
            new DMDisplayError(
              errorParser.parseMessage(e),
              'T_DEVICE_MANAGEMENT_DISPLAY_INIT_ERROR',
              {
                cause: e
              }
            )
          )
        })

      core?.on('deviceStateChange', ({ msgType }) => {
        logService.info(`${LOG_PREFIX} Device state change event received`, {
          code: 'DM_DEVICE_STATE_CHANGE_EVENT_RECEIVED',
          details: {
            msgType,
            autoSetDeviceAccessToken: autoSetDeviceAccessToken.value
          }
        })
        if (msgType === 'location_work_time_update') {
          handleWorkingTimeUpdate(appRunUuid)
          return
        }

        if (msgType === 'nxg_access_token_updated' && autoSetDeviceAccessToken.value) {
          handleDeviceAccessTokenChange('tokenUpdateEvent')
          return
        }
      })

      core?.on('coreReady', () => {
        logService.info(`${LOG_PREFIX} Core ready event received`, {
          code: 'DM_CORE_READY_EVENT_RECEIVED',
          details: {
            autoSetDeviceAccessToken: autoSetDeviceAccessToken.value
          }
        })
        if (autoSetDeviceAccessToken.value) {
          handleDeviceAccessTokenChange('coreReadyEvent')
        }
      })
    })
  })

/**
 * @throws {DMCoreError}
 */
const getCore = () => {
  if (!core) {
    throw new DMCoreError('Core is not initialized', 'DM_CORE_NOT_INITIALIZED')
  }

  return core
}

export default { initDeviceManagement, getCore }
